/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { Formik } from 'formik'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { useServiceContext } from '../../root'
import { useRcmTableData } from '../../hooks/useRcmTableData'
import { Select } from '../../components/Select'
import { SystemFolderType } from '../../constants/fsi'
import { DocumentsViewType } from './types'

interface ReportsTableFilterProps {
  viewType: DocumentsViewType
  isFilterCollapsed: boolean
}

interface FilterValues {
  createdById: string | null
  type: string | null
  createdAtFrom: Date | null
  createdAtTo: Date | null
  showObsoleted: boolean
  viewType: DocumentsViewType
}

export const ReportsTableFilter: React.FC<ReportsTableFilterProps> = ({ viewType, isFilterCollapsed }) => {
  const context = useServiceContext()
  const tableData = useRcmTableData()
  const {
    service: {
      filter: { translate }
    },
    api
  } = context

  const todayDate = new Date()
  const persistenceKey = tableData.persistenceKey + '_filter'

  const defaultFilter: FilterValues = {
    createdById: null,
    type: (viewType as string) === SystemFolderType.EMAIL_UPLOADS ? SystemFolderType.EMAIL_UPLOADS : null,
    createdAtFrom: null,
    createdAtTo: null,
    showObsoleted: false,
    viewType: viewType
  }

  const [filter, setFilter] = useState<FilterValues | null>(null)
  const [options, setOptions] = useState<{
    users: Array<{ value: any; label: string }>
    types: Array<{ value: any; label: string }>
  }>({
    users: [],
    types: []
  })

  useEffect(() => {
    // Initialize with default filter on mount
    setFilter(defaultFilter)
    doFilter(defaultFilter)
  }, [])

  useEffect(() => {
    const promises: Promise<void>[] = []

    // Load users
    promises.push(
      api.users.getAssembledUsersGroups().then((data: any) => {
        const userOptions = data.users.map((user: any) => ({
          value: user.id,
          label: user.name
        }))
        setOptions((prevState) => ({
          ...prevState,
          users: userOptions
        }))
      })
    )

    // Load report types (except for EMAIL_UPLOADS)
    if ((viewType as string) !== SystemFolderType.EMAIL_UPLOADS) {
      promises.push(
        api.reports.getReportTypesMap(viewType as string).then((data: any) => {
          const typeOptions = Object.entries(data).map(([key, value]) => ({
            value: key,
            label: value as string
          }))
          setOptions((prevState) => ({
            ...prevState,
            types: typeOptions
          }))
        })
      )
    }

    Promise.all(promises)
  }, [viewType])

  const doFilter = (values: FilterValues) => {
    tableData.setFilter(values)
    // Store filter in localStorage directly
    try {
      localStorage.setItem(persistenceKey, JSON.stringify(values))
    } catch (e) {
      console.error('Failed to persist filter', e)
    }
  }

  if (isFilterCollapsed || !filter) {
    return null
  }

  return (
    <div className="filter-wrapper">
      <Formik
        initialValues={filter}
        onReset={() => {
          setFilter(defaultFilter)
          doFilter(defaultFilter)
        }}
        enableReinitialize={true}
        onSubmit={(values) => {
          doFilter(values)
        }}
      >
        {({ values, handleSubmit, handleReset, setFieldValue }) => {
          const isDefaultFilter =
            values.createdById === defaultFilter.createdById &&
            values.type === defaultFilter.type &&
            values.createdAtFrom === defaultFilter.createdAtFrom &&
            values.createdAtTo === defaultFilter.createdAtTo &&
            values.showObsoleted === defaultFilter.showObsoleted

          return (
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-xs-4">
                  {(viewType as string) !== SystemFolderType.EMAIL_UPLOADS && (
                    <div className="form-group">
                      <label htmlFor="report-type-select" className="report-filter-label" style={{ fontSize: '14px' }}>
                        {translate(
                          [
                            SystemFolderType.DOCUMENT_MIGRATIONS_AND_EXPORTS,
                            SystemFolderType.SETTINGS_AND_SYSTEM_EXPORTS_IMPORTS
                          ].includes(viewType as any)
                            ? 'ITEM_TYPE_LABEL'
                            : 'REPORTS_FILTER_LABEL_REPORT_TYPE'
                        )}
                      </label>
                      <Select
                        id="report-type-select"
                        name="type"
                        value={values.type}
                        placeholder={translate('OPTION_ALL_TYPES')}
                        options={options.types}
                        onChange={(option: any) => {
                          setFieldValue('type', option ? option.value : null)
                        }}
                        searchable
                      />
                    </div>
                  )}

                  <div className="form-group">
                    <label className="report-filter-label">
                      <input
                        type="checkbox"
                        name="showObsoleted"
                        checked={values.showObsoleted}
                        onChange={(e) => setFieldValue('showObsoleted', e.target.checked)}
                        style={{ marginRight: '8px' }}
                      />
                      {translate('REPORTS_FILTER_LABEL_SHOW_OBSOLETED_REPORTS')}
                    </label>
                  </div>
                </div>
                <div className="col-xs-4">
                  <label className="report-filter-label" style={{ fontSize: '14px' }}>
                    {translate('CREATED_ON')}
                  </label>
                  <div className="date-range-container">
                    <div className="form-group">
                      <label htmlFor="created-from" className="report-filter-label">
                        {translate('SEARCH_FROM')}{' '}
                      </label>
                      <DatePicker
                        id="created-from"
                        name="createdAtFrom"
                        selected={values.createdAtFrom}
                        onChange={(date) => setFieldValue('createdAtFrom', date)}
                        maxDate={values.createdAtTo || todayDate}
                        todayButton="Today"
                        dateFormat="MMM dd, yyyy"
                        showYearDropdown
                        className="form-control field-control w100p"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="created-to" className="report-filter-label">
                        {translate('SEARCH_TO')}{' '}
                      </label>
                      <DatePicker
                        id="created-to"
                        name="createdAtTo"
                        selected={values.createdAtTo}
                        onChange={(date) => setFieldValue('createdAtTo', date)}
                        minDate={values.createdAtFrom}
                        maxDate={todayDate}
                        todayButton="Today"
                        dateFormat="MMM dd, yyyy"
                        showYearDropdown
                        className="form-control field-control w100p"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-xs-4">
                  <div className="form-group">
                    <label htmlFor="created-by-select" className="report-filter-label" style={{ fontSize: '14px' }}>
                      {translate('CREATED_BY')}
                    </label>
                    <Select
                      id="created-by-select"
                      name="createdById"
                      value={values.createdById}
                      placeholder={translate('SELECT_USER')}
                      options={options.users}
                      onChange={(option: any) => {
                        setFieldValue('createdById', option ? option.value : null)
                      }}
                      searchable
                    />
                  </div>
                </div>
              </div>
              <div>
                <button type="submit" disabled={tableData.isLoading} className="btn btn-primary">
                  {translate('BTN_FILTER')}
                </button>
                <button
                  type="button"
                  disabled={isDefaultFilter}
                  className="btn btn-chita btn-link"
                  onClick={() => handleReset()}
                >
                  {translate('BTN_CLEAR_FILTER')}
                </button>
              </div>
            </form>
          )
        }}
      </Formik>
    </div>
  )
}
